import { supabase } from "@/lib/supabase";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";

/**
 * AppointmentService provides data access to the appointment_results table in Supabase.
 * Manages appointment outcome records and scheduling confirmations.
 */

export interface AppointmentCreate {
  appointmentId: string;
  leadId: string;
  quoteId?: string;
  scheduledAt: string;
  status?: "scheduled" | "completed" | "cancelled" | "no_show" | "rescheduled";
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  createdBy?: string;
  notes?: string;
  organizationId?: string;
}

export const AppointmentService = {
  /**
   * Create a new appointment result record
   */
  async create(data: AppointmentCreate) {
    let organizationId: string | null = data.organizationId ?? null;
    if (!organizationId) {
      try {
        organizationId = await getCurrentOrganizationId();
      } catch {
        organizationId = null;
      }
    }

    let createdBy = data.createdBy ?? "system";
    if (!data.createdBy) {
      const { data: authData } = await supabase.auth.getUser();
      createdBy = authData.user?.id ?? "system";
    }

    const { data: result, error } = await supabase
      .from("appointment_results")
      .insert([{
        appointment_id: data.appointmentId,
        lead_id: data.leadId,
        quote_id: data.quoteId || null,
        scheduled_at: data.scheduledAt,
        status: data.status || "scheduled",
        contact_name: data.contactName || null,
        contact_phone: data.contactPhone || null,
        contact_email: data.contactEmail || null,
        created_by: createdBy,
        organization_id: organizationId,
        notes: data.notes || null,
      }])
      .select()
      .single();

    return { data: result, error };
  },

  /**
   * Get all appointment results
   */
  async getAll() {
    const { data, error } = await supabase
      .from("appointment_results")
      .select("*")
      .order("scheduled_at", { ascending: false });

    return { data, error };
  },

  /**
   * Get appointment results by lead ID
   */
  async getByLeadId(leadId: string) {
    const { data, error } = await supabase
      .from("appointment_results")
      .select("*")
      .eq("lead_id", leadId)
      .order("scheduled_at", { ascending: false });

    return { data, error };
  },

  /**
   * Update an appointment result (e.g., status change, outcome recording)
   */
  async update(id: string, updates: Record<string, unknown>) {
    const { data, error } = await supabase
      .from("appointment_results")
      .update(updates)
      .eq("id", id)
      .select()
      .single();

    return { data, error };
  },

  /**
   * Update appointment result by appointment_id (natural key)
   */
  async upsertByAppointmentId(appointmentId: string, updates: Record<string, unknown>) {
    const { data, error } = await supabase
      .from("appointment_results")
      .upsert(
        { appointment_id: appointmentId, ...updates },
        { onConflict: "appointment_id" }
      )
      .select()
      .single();

    return { data, error };
  },

  /**
   * Delete an appointment result
   */
  async delete(id: string) {
    const { error } = await supabase
      .from("appointment_results")
      .delete()
      .eq("id", id);

    return { error };
  },
};
